import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '@/store'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { sendPhoneCodeThunk, verifyPhoneCodeThunk, updateUser } from '@/redux/userSlice'

/** Phone number and its verification; embedded drops the card around it */
const PhoneVerification = ({ embedded = false }: { embedded?: boolean }) => {
  const user = useSelector((state: RootState) => state.user.currentUser)
  const [phone, setPhone] = useState(user?.phone || '')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch<AppDispatch>()
  const { toast } = useToast()

  useEffect(() => {
    if (user?.phone) {
      setPhone(user.phone)
    }
  }, [user?.phone])

  const handleSendCode = async () => {
    if (!phone || phone.length < 10) {
      toast({
        title: 'Invalid phone number',
        description: 'Please enter a valid phone number.',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)
    try {
      await dispatch(sendPhoneCodeThunk(phone)).unwrap()
      setCodeSent(true)
      toast({
        title: 'Code sent',
        description: `Verification code sent to ${phone}`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error || 'Failed to send verification code.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!code || code.length < 6) {
      toast({
        title: 'Invalid code',
        description: 'Please enter a 6-digit code.',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)
    try {
      await dispatch(verifyPhoneCodeThunk({ phone, code })).unwrap()

      // Update local user state
      dispatch(updateUser({ phone, phone_verified: true }))

      setCodeSent(false)
      setCode('')
      toast({
        title: 'Success',
        description: 'Phone number verified successfully!',
      })
    } catch (error: any) {
      toast({
        title: 'Verification failed',
        description: error || 'Invalid code.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const fields = (
    <div className="space-y-4 max-w-sm">
      <div className="flex flex-col space-y-2">
        <Label htmlFor="phone" className="text-gray-700">
          Phone Number
        </Label>
        <div className="flex items-center space-x-2">
          <Input
            id="phone"
            placeholder="Phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={user?.phone_verified || codeSent || isLoading}
          />

          {user?.phone_verified ? (
            <span className="text-green-500 flex items-center px-2 font-medium whitespace-nowrap">
              ✓ Verified
            </span>
          ) : (
            !codeSent && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSendCode}
                disabled={isLoading || !phone}
                className="bg-[#C0FD72] text-black hover:bg-[#b0ef62] font-semibold border-0"
              >
                {isLoading ? 'Sending...' : 'Send Code'}
              </Button>
            )
          )}
        </div>
      </div>

      {codeSent && !user?.phone_verified && (
        <div className="flex flex-col space-y-2 animate-in fade-in slide-in-from-top-2">
          <Label htmlFor="code" className="text-gray-700">
            Verification Code
          </Label>
          <div className="flex items-center space-x-2">
            <Input
              id="code"
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
            <Button
              type="button"
              onClick={handleVerifyCode}
              disabled={isLoading || !code}
              className="bg-[#C0FD72] text-black hover:bg-[#b0ef62] font-semibold"
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCodeSent(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
          <p className="text-xs text-gray-600">Check your messages for the code.</p>
        </div>
      )}
    </div>
  )

  if (embedded) return fields

  return (
    <Card className="mt-6 bg-card border-foreground/10 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
      <CardHeader>
        <CardTitle className="text-foreground">Phone Verification</CardTitle>
        <CardDescription className="text-gray-600">
          Verify your phone number to receive important notifications.
        </CardDescription>
      </CardHeader>
      <CardContent>{fields}</CardContent>
    </Card>
  )
}

export default PhoneVerification
